import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Image, Space, Typography, Badge, Dropdown, Modal, message } from 'antd';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useQuery, useMutation } from '@apollo/client';
import useLogout from '../hooks/useLogout.jsx';
import { GET_UNREAD_NOTIFICATIONS_COUNT, GET_USER_NOTIFICATIONS } from '../graphql/queries/notifications.js';
import { MARK_NOTIFICATION_AS_READ, MARK_ALL_NOTIFICATIONS_AS_READ } from '../graphql/mutations/notifications.js';
import {
  UserOutlined,
  ShoppingCartOutlined,
  AppstoreOutlined,
  TeamOutlined,
  TagsOutlined,
  CreditCardOutlined,
  DollarOutlined,
  ContactsOutlined,
  ProfileOutlined,
  DashboardOutlined,
  WhatsAppOutlined
} from '@ant-design/icons';

const { Header, Content, Sider } = Layout;

const AppLayout = () => {
  const location = useLocation();
  const logout = useLogout();
  
  // Obter email do usuário do localStorage
  const userEmail = localStorage.getItem('uid');

  // Buscar quantidade de notificações não lidas do backend
  const { data: notificationsData, loading: notificationsLoading, error: notificationsError, refetch: refetchUnreadCount } = useQuery(GET_UNREAD_NOTIFICATIONS_COUNT, {
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore' // Ignora erros para não quebrar a interface
  });

  // Buscar notificações do usuário
  const { data: userNotificationsData, loading: userNotificationsLoading, error: userNotificationsError, refetch: refetchNotifications } = useQuery(GET_USER_NOTIFICATIONS, {
    variables: { limit: 5, offset: 0 },
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore'
  });

  // Mutation para marcar notificação como lida
  const [markNotificationAsRead] = useMutation(MARK_NOTIFICATION_AS_READ, {
    onCompleted: async (data) => {
      if (data.markNotificationAsRead.errors && data.markNotificationAsRead.errors.length > 0) {
        message.error('Erro ao marcar notificação como lida: ' + data.markNotificationAsRead.errors.join(', '));
      } else {
        try {
          // Refetch das queries para atualizar contador e lista
          await Promise.all([
            refetchUnreadCount(),
            refetchNotifications()
          ]);
          message.success('Notificação marcada como lida!');
        } catch (refetchError) {
          console.error('Erro ao atualizar notificações:', refetchError);
          message.warning('Notificação marcada como lida, mas houve erro ao atualizar a lista');
        }
      }
    },
    onError: (error) => {
      message.error('Erro ao marcar notificação como lida');
      console.error('Erro:', error);
    }
  });

  // Mutation para marcar todas as notificações como lidas
  const [markAllNotificationsAsRead] = useMutation(MARK_ALL_NOTIFICATIONS_AS_READ, {
    onCompleted: async (data) => {
      if (data.markAllNotificationsAsRead.success) {
        try {
          // Refetch das queries para atualizar contador e lista
          await Promise.all([
            refetchUnreadCount(),
            refetchNotifications()
          ]);
          message.success(data.markAllNotificationsAsRead.message || 'Todas as notificações foram marcadas como lidas!');
        } catch (refetchError) {
          console.error('Erro ao atualizar notificações:', refetchError);
          message.warning('Notificações marcadas como lidas, mas houve erro ao atualizar a lista');
        }
      } else {
        message.error('Erro ao marcar todas as notificações como lidas');
      }
    },
    onError: (error) => {
      message.error('Erro ao marcar todas as notificações como lidas');
      console.error('Erro:', error);
    }
  });

  // useEffect para refetch automático das notificações quando a rota muda
  useEffect(() => {
    const refetchNotificationsData = async () => {
      try {
        await Promise.all([
          refetchUnreadCount(),
          refetchNotifications()
        ]);
      } catch (error) {
        console.error('Erro ao atualizar notificações na navegação:', error);
      }
    };

    // Refetch quando a rota muda
    refetchNotificationsData();
  }, [location.pathname, refetchUnreadCount, refetchNotifications]);

  // Usar valor do backend ou fallback para 0 em caso de erro/loading
  const unreadCount = notificationsData?.unreadNotificationsCount || 0;
  const notifications = userNotificationsData?.userNotifications || [];

  // Função para confirmar e marcar notificação como lida
  const handleMarkAsRead = (notificationId, notificationTitle) => {
    Modal.confirm({
      title: 'Marcar como lida',
      content: `Deseja marcar esta notificação como lida?`,
      okText: 'Sim',
      cancelText: 'Cancelar',
      onOk: () => {
        markNotificationAsRead({
          variables: { id: notificationId }
        });
      }
    });
  };

  // Função para confirmar e marcar todas as notificações como lidas
  const handleMarkAllAsRead = () => {
    Modal.confirm({
      title: 'Marcar todas como lidas',
      content: 'Deseja marcar todas as notificações como lidas?',
      okText: 'Sim',
      cancelText: 'Cancelar',
      onOk: () => {
        markAllNotificationsAsRead();
      }
    });
  };

  // Função para formatar timestamp
  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const notificationDate = new Date(timestamp);
    const diffInMinutes = Math.floor((now - notificationDate) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Agora';
    if (diffInMinutes < 60) return `${diffInMinutes} min atrás`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hora${diffInHours > 1 ? 's' : ''} atrás`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} dia${diffInDays > 1 ? 's' : ''} atrás`;
  };

  // Criar items do dropdown de notificações
  const createNotificationItems = () => {
    const items = [];

    if (userNotificationsLoading) {
      items.push({
        key: 'loading',
        label: (
          <div style={{ width: 300, padding: '16px 0', textAlign: 'center' }}>
            <Typography.Text type="secondary">Carregando notificações...</Typography.Text>
          </div>
        ),
        disabled: true
      });
    } else if (notifications.length === 0) {
      items.push({
        key: 'no-notifications',
        label: (
          <div style={{ width: 300, padding: '16px 0', textAlign: 'center' }}>
            <Typography.Text type="secondary">Nenhuma notificação</Typography.Text>
          </div>
        ),
        disabled: true
      });
    } else {
      // Adicionar notificações reais do backend
      notifications.forEach((notification, index) => {
        items.push({
          key: `notification-${notification.id}`,
          label: (
            <div 
              style={{ 
                width: 300, 
                padding: '8px 0',
                cursor: notification.read ? 'default' : 'pointer',
                opacity: notification.read ? 0.6 : 1
              }}
              onClick={() => {
                if (!notification.read) {
                  handleMarkAsRead(notification.id, notification.title);
                }
              }}
            >
              <div style={{ 
                fontWeight: notification.read ? 'normal' : 'bold', 
                marginBottom: 4,
                color: notification.read ? '#666' : '#000'
              }}>
                {notification.title}
                {!notification.read && (
                  <span style={{ 
                    color: '#1890ff', 
                    fontSize: '10px', 
                    marginLeft: 8,
                    fontWeight: 'normal'
                  }}>
                    • Nova
                  </span>
                )}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: 4, whiteSpace: 'pre-line' }}>
                {notification.message}
              </div>
              <div style={{ fontSize: '11px', color: '#999' }}>
                {formatTimestamp(notification.createdAt)}
                {!notification.read && (
                  <span style={{ 
                    color: '#1890ff', 
                    fontSize: '10px', 
                    marginLeft: 8 
                  }}>
                    (Clique para marcar como lida)
                  </span>
                )}
              </div>
            </div>
          ),
        });
      });
    }

    // Adicionar separador e ações se houver notificações
    if (notifications.length > 0) {
      items.push({
        type: 'divider',
      });
      items.push({
        key: 'mark-all-read',
        label: 'Marcar todas como lidas',
        onClick: handleMarkAllAsRead,
      });
    }

    return items;
  };

  const menuItems = [
    {
      key: 'dashboard',
      label: <Link to="/dashboard">Dashboard</Link>,
      icon: <DashboardOutlined  />
    },
    {
      key: 'campaigns',
      label: <Link to="/campaigns">Campanhas</Link>,
      icon: <WhatsAppOutlined />
    },
    {
      key: 'orders',
      label: <Link to="/orders">Pedidos</Link>,
      icon: <ShoppingCartOutlined />
    },
    {
      type: 'divider'
    },
    {
      key: 'account-settings',
      label: <Link to="/account-settings">Conta</Link>,
      icon: <UserOutlined />
    },
    {
      key: 'seller-settings',
      label: <Link to="/seller-settings">Vendedor</Link>,
      icon: <ContactsOutlined />
    },
    {
      key: 'priceSegmentations',
      label: <Link to="/priceSegmentations">Tabelas de preço</Link>,
      icon: <AppstoreOutlined />
    },
    
    {
      key: 'products',
      label: <Link to="/products">Produtos</Link>,
      icon: <AppstoreOutlined />
    },
    {
      key: 'customers',
      label: <Link to="/customers">Clientes</Link>,
      icon: <TeamOutlined />
    },
    {
      key: 'groups',
      label: <Link to="/groups">Grupos</Link>,
      icon: <TagsOutlined />
    },
    {
      key: 'tags',
      label: <Link to="/tags">Tags</Link>,
      icon: <TagsOutlined />
    },

    {
      key: 'paymentMethods',
      label: <Link to="/paymentMethods">Formas de pagamento</Link>,
      icon: <CreditCardOutlined />
    },
    {
      key: 'paymentConditions',
      label: <Link to="/paymentConditions">Condições de pagamento</Link>,
      icon: <DollarOutlined />
    },
    {
      key: 'paymentProfiles',
      label: <Link to="/paymentProfiles">Perfis de pagamento</Link>,
      icon: <ProfileOutlined  />
    },
  ];

  const getDefaultSelectedKey = () => {
    const path = location.pathname;
    const menuItem = menuItems.find(item => item.key && path.includes(item.key));
    return menuItem ? menuItem.key : 'dashboard';
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header className="header" style={{ backgroundColor: '#fff', padding: '0 20px' }}>
        <div className="logo" style={{ float: 'left', marginLeft: '-10px' }}>
          <Image src={'logo192.png'} alt="Logo" preview={false} width={200} />
        </div>
        <Space style={{ float: 'right', marginTop: '10px' }}>
          {/* Notification Dropdown */}
          <Dropdown
            menu={{
              items: createNotificationItems(),
            }}
            placement="bottomRight"
            trigger={['click']}
          >
            <Badge count={unreadCount > 0 ? unreadCount : 0} size="small">
              <Button
                type="text"
                icon={<Bell size={20} />}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: '#666',
                  marginRight: -4
                }}
              />
            </Badge>
          </Dropdown>
          
          {userEmail && (
            <Space style={{ marginRight: 8 }}>
              <UserOutlined style={{ color: '#666' }} />
              <Typography.Text style={{ color: '#666' }}>
                {userEmail}
              </Typography.Text>
            </Space>
          )}
          <Button type="link" onClick={logout}>
            Sair
          </Button>
        </Space>
      </Header>
      <Layout>
        <Sider width={255} className="site-layout-background">
          <Menu
            mode="inline"
            selectedKeys={[getDefaultSelectedKey()]}
            style={{ height: '100%', borderRight: 0 }}
            items={menuItems}
          />
        </Sider>
        <Layout style={{ padding: '0 24px 24px' }}>
          <Content
            className="site-layout-background"
            style={{
              padding: 24,
              margin: 0,
              minHeight: 280,
            }}
          >
            <Outlet />
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
